import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Bell, X, Check, CheckCircle, AlertCircle, CreditCard, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiFetch, apiGet } from '../lib/apiClient';
import { API_ROUTES } from '../lib/apiRoutes';

const POLL_INTERVAL = 30000; // 30s

const TYPE_ICONS = {
  payment: CreditCard,
  drawdown: AlertCircle,
  record: CheckCircle,
  subscription: AlertCircle,
  system: Info,
};

const TYPE_COLORS = {
  payment: 'text-green-400',
  drawdown: 'text-red-400',
  record: 'text-amber-400',
  subscription: 'text-blue-400',
  system: 'text-gray-400',
};

export default function NotificationCenter() {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState(null);
  const ref = useRef(null);
  const buttonRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const data = await apiGet(`${API_ROUTES.app.notifications}/unread-count`);
      setUnreadCount(data.unreadCount || 0);
    } catch {}
  }, [token]);

  const fetchAll = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await apiGet(API_ROUTES.app.notifications);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch {} finally { setLoading(false); }
  };

  // Initial + polling
  useEffect(() => {
    fetchUnreadCount();
    const t = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => clearInterval(t);
  }, [fetchUnreadCount]);

  // Fetch full list when opening
  useEffect(() => {
    if (open) fetchAll();
  }, [open]); // eslint-disable-line

  // Click-outside (check both bell wrapper and portal dropdown)
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      // Check if clicked inside portal dropdown
      const dropdown = document.querySelector('[data-notification-dropdown]');
      if (dropdown && dropdown.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markAllRead = async () => {
    await apiFetch(`${API_ROUTES.app.notifications}/read-all`, {
      method: 'PATCH',
    });
    fetchAll();
  };

  const markRead = async (id) => {
    await apiFetch(`${API_ROUTES.app.notifications}/${id}/read`, {
      method: 'PATCH',
    });
    fetchAll();
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toISOString().split('T')[0];
  };

  return (
    <div ref={ref} className="relative">
      <button
        ref={buttonRef}
        onClick={() => {
          if (!open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            // Position dropdown below+right of the bell, but keep it on-screen
            const dropdownWidth = 340;
            const left = Math.min(rect.left, window.innerWidth - dropdownWidth - 16);
            setPosition({ top: rect.bottom + 8, left });
          }
          setOpen(!open);
        }}
        className="relative p-2 text-gray-400 hover:text-gray-200 rounded-lg hover:bg-white/5 transition-all"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </button>

      {open && position && createPortal(
        <div
          data-notification-dropdown
          className="fixed w-[340px] max-h-[500px] bg-dark-card border border-dark-border rounded-xl shadow-2xl shadow-black/50 z-[9999] overflow-hidden flex flex-col animate-fade-in"
          style={{ top: position.top, left: position.left }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border">
            <h3 className="text-sm font-semibold text-gray-200">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-emerald-400 hover:text-emerald-300">
                Mark all read
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-8 text-center text-gray-500 text-sm">Loading...</div>
            ) : !notifications.length ? (
              <div className="p-8 text-center">
                <Bell size={32} className="mx-auto mb-2 text-gray-700" />
                <p className="text-gray-500 text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = TYPE_ICONS[n.type] || Info;
                const color = TYPE_COLORS[n.type] || 'text-gray-400';
                const Wrapper = n.link ? Link : 'div';
                return (
                  <Wrapper
                    key={n.id}
                    to={n.link || undefined}
                    onClick={() => { markRead(n.id); if (n.link) setOpen(false); }}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-dark-border/50 hover:bg-white/[0.02] cursor-pointer transition-colors ${!n.is_read && !n.isRead ? 'bg-emerald-500/[0.03]' : ''}`}
                  >
                    <div className={`w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 ${color}`}>
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-200">{n.title}</p>
                      {n.message && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>}
                      <p className="text-[10px] text-gray-600 mt-1">{formatTime(n.createdAt || n.created_at)}</p>
                    </div>
                    {!(n.is_read || n.isRead) && <span className="w-2 h-2 bg-emerald-400 rounded-full flex-shrink-0 mt-1" />}
                  </Wrapper>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
